import React, { useCallback, useState } from 'react';
import ChevronDown from '@uber/base-icons/chevron-down';
import ChevronRight from '@uber/base-icons/chevron-right';
import { useStyletron } from 'baseui';
import { Button, KIND, SHAPE, SIZE } from 'baseui/button';
import { Tag } from 'baseui/tag';
import { getTagColors } from './utils/tag-color-utils';
import { GroupHeaderContainer, ListItemContent, NestedItemsContainer } from './styled-components';
import type { ItemGroupProps } from './types';
export const ItemGroup = ({
  label,
  itemCount,
  tagColor,
  children,
  styleOverrides
}: ItemGroupProps) => {
  const [css, theme] = useStyletron();
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpanded = useCallback(() => {
    setIsExpanded(previous => !previous);
  }, []);
  const {
    tagBackground,
    tagTextColor,
    hierarchy
  } = getTagColors({
    tagColor,
    isExpanded
  });
  const itemCountLabel = `${itemCount} item${itemCount > 1 ? 's' : ''}`;
  const tagOverrides = {
    Root: {
      style: {
        margin: 0,
        ...(tagBackground && {
          backgroundColor: tagBackground
        }),
        maxWidth: '180px'
      }
    },
    Text: {
      style: {
        overflow: ('hidden' as const),
        textOverflow: 'ellipsis',
        whiteSpace: ('nowrap' as const)
      }
    }
  };
  const showTopBorder = !isExpanded && !styleOverrides?.hideBorderTop;
  return <div>
      {showTopBorder && <div style={{
      borderBottom: `1px solid ${theme.colors.borderOpaque}`
    }} />}
      <GroupHeaderContainer $isExpanded={isExpanded} data-testid={isExpanded ? 'collapse-group-button' : 'expand-group-button'} role="button" tabIndex={0} onClick={toggleExpanded}>
        <ListItemContent>
          <Tag closeable={false} hierarchy={hierarchy} overrides={tagOverrides}>
            <div className={css({
            ...theme.typography.LabelXSmall,
            color: tagTextColor ?? 'inherit'
          })}>
              {label}
            </div>
          </Tag>
          <div className={css({
          ...theme.typography.ParagraphSmall,
          color: theme.colors.contentSecondary,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        })}>
            {'• '}
            {itemCountLabel}
          </div>
        </ListItemContent>
        <Button kind={KIND.tertiary} size={SIZE.mini} shape={SHAPE.circle} onClick={(event: React.SyntheticEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        toggleExpanded();
      }}>
          {isExpanded ? <ChevronDown height={theme.sizing.scale500} width={theme.sizing.scale500} /> : <ChevronRight height={theme.sizing.scale500} width={theme.sizing.scale500} />}
        </Button>
      </GroupHeaderContainer>
      {isExpanded && <NestedItemsContainer style={{
      padding: `0 ${theme.sizing.scale400}`,
      boxSizing: 'border-box'
    }}>
          {children}
        </NestedItemsContainer>}
    </div>;
};